import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Grid, Typography, CircularProgress } from '@mui/material';
import { Colors } from '../config/default';
import AttachmentDropzone from '../components/attachmentDropzone';
import { Close } from '@mui/icons-material';
import { useToast } from '../toast/toastContext';
import { uploadAttachments } from '../services/services';
import { isEmpty } from 'lodash';
import { truncateFileName } from '../services/common';
import '../../src/attachment.css';

export default function AttachmentUpload({ refreshAttachments, setOpenUploadModal, id }) {
  const { showToast } = useToast();
  const [files, setFiles] = useState([]);
  const [instrumentIds, setInstrumentIds] = useState([]);
  const [uploading, setUploading] = useState(false);

  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const companyId = userDetails?.company?._id;

  const generateUniqueId = () => `INST-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  useEffect(() => {
    setInstrumentIds(files?.map(() => generateUniqueId()));
  }, [files]);

  const removeFile = (fileName) => {
    setFiles(files.filter((file) => file.name !== fileName));
  };

  const UploadAttachment = async () => {
    setUploading(true);
    if (isEmpty(files)) {
      showToast('Please upload at least one file.', 'error');
      return;
    }
    setUploading(true);
    const result = await uploadAttachments(files, [id], instrumentIds, companyId);
    if (result?.status === 200) {
      showToast(result?.data?.metadata?.message, 'success');
      setFiles([]);
      setInstrumentIds([]);
      await refreshAttachments();
      setOpenUploadModal(false);
    } else {
      showToast(
        result?.response?.data?.metadata?.message || result?.data?.metadata?.message,
        'error'
      );
    }
    setUploading(false);
  };

  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 2,
        backgroundColor: Colors.NAV_BLACK,
        borderRadius: '20px',
        height: '73vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Typography sx={{ color: Colors.TOURQUISE, mb: '1rem' }}>Attachments</Typography>

      <Box sx={{ mb: 2 }}>
        <AttachmentDropzone setFiles={setFiles} />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pr: 1 
        }}>
        {isEmpty(files) ? (
          <Grid container justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
            <Typography sx={{ color: Colors.TEXTGREY }}>No Attachments</Typography>
          </Grid>
        ) : (
          files.map((file, index) => (
            <Box
              key={index}
              sx={{
                border: '2px dashed #5B5B5B',
                borderRadius: '20px',
                p: 2,
                position: 'relative'
              }}>
              <Close
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: Colors.TEXTGREY,
                  cursor: 'pointer'
                }}
                onClick={() => removeFile(file?.name)}
              />
              <Typography variant="body1" sx={{ color: Colors.WHITE, mt: 1 }}>
                <strong>File Name:</strong> {truncateFileName(file?.name, 19)}
              </Typography>
            </Box>
          ))
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button
          disabled={uploading || isEmpty(files)}
          fullWidth
          sx={{
            color: '#fff',
            backgroundColor: Colors.DEEPBLUE,
            borderRadius: '10px',
            '&:hover': { background: Colors.DEEPBLUE },
            '&:disabled': { backgroundColor: Colors.Canvas_BG }
          }}
          onClick={UploadAttachment}>
          {uploading ? <CircularProgress size={25} color="inherit" /> : 'Attach'}
        </Button>
      </Box>
    </Grid>
  );
}
